var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { Slider, Box, TextField, Typography } from '@mui/material';
import { theme } from '../styles/theme';
var MILLISECONDS_PER_DAY = 86400000;
/**
 * Formats a timestamp to ISO date string (YYYY-MM-DD)
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @returns {string} - ISO date string
 */
var formatDate = function (timestamp) {
    var date = new Date(timestamp);
    return date.toISOString().split('T')[0];
};
/**
 * Formats a timestamp to display date string (MM/DD/YYYY)
 * Uses UTC to avoid timezone offset issues
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @returns {string} - Formatted date string
 */
var formatDisplayDate = function (timestamp) {
    var date = new Date(timestamp);
    var month = String(date.getUTCMonth() + 1).padStart(2, '0');
    var day = String(date.getUTCDate()).padStart(2, '0');
    var year = date.getUTCFullYear();
    return "".concat(month, "/").concat(day, "/").concat(year);
};
var dateInputStyles = {
    flex: 1,
    '& .MuiOutlinedInput-root': {
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
        },
    },
    '& .MuiOutlinedInput-input': {
        padding: '2px 6px',
        fontSize: '0.8125rem',
    },
};
export var DateRangeSlider = function (_a) {
    var startDate = _a.startDate, endDate = _a.endDate, minDate = _a.minDate, maxDate = _a.maxDate, _b = _a.disabled, disabled = _b === void 0 ? false : _b, onDateChange = _a.onDateChange;
    var initialStartDate = new Date(startDate).getTime();
    var initialEndDate = new Date(endDate).getTime();
    var minDateTimestamp = new Date(minDate).getTime();
    var maxDateTimestamp = new Date(maxDate).getTime();
    var _c = useState([initialStartDate, initialEndDate]), dateRange = _c[0], setDateRange = _c[1];
    var _d = useState(formatDate(initialStartDate)), startDateInput = _d[0], setStartDateInput = _d[1];
    var _e = useState(formatDate(initialEndDate)), endDateInput = _e[0], setEndDateInput = _e[1];
    var startDateTimeoutRef = useRef(null);
    var endDateTimeoutRef = useRef(null);
    useEffect(function () {
        var newStart = new Date(startDate).getTime();
        var newEnd = new Date(endDate).getTime();
        setDateRange([newStart, newEnd]);
        setStartDateInput(formatDate(newStart));
        setEndDateInput(formatDate(newEnd));
    }, [startDate, endDate]);
    useEffect(function () {
        return function () {
            if (startDateTimeoutRef.current) {
                clearTimeout(startDateTimeoutRef.current);
            }
            if (endDateTimeoutRef.current) {
                clearTimeout(endDateTimeoutRef.current);
            }
        };
    }, []);
    var handleDateChange = function (_, newValue) {
        setDateRange(newValue);
        setStartDateInput(formatDate(newValue[0]));
        setEndDateInput(formatDate(newValue[1]));
    };
    var handleDateChangeCommitted = function (_, newValue) {
        var newStartTimestamp = newValue[0], newEndTimestamp = newValue[1];
        var newStartDate = formatDate(newStartTimestamp);
        var newEndDate = formatDate(newEndTimestamp);
        onDateChange(newStartDate, newEndDate);
    };
    var handleStartDateChange = function (event) {
        var newValue = event.target.value;
        setStartDateInput(newValue);
        if (!newValue) {
            return;
        }
        var newStartDate = new Date(newValue).getTime();
        if (newStartDate >= minDateTimestamp && newStartDate <= dateRange[1] && !isNaN(newStartDate)) {
            setDateRange([newStartDate, dateRange[1]]);
            if (startDateTimeoutRef.current) {
                clearTimeout(startDateTimeoutRef.current);
            }
            // add 1 second debounce before triggering the update
            startDateTimeoutRef.current = setTimeout(function () {
                onDateChange(formatDate(newStartDate), formatDate(dateRange[1]));
            }, 1000);
        }
    };
    var handleEndDateChange = function (event) {
        var newValue = event.target.value;
        setEndDateInput(newValue);
        if (!newValue) {
            return;
        }
        var newEndDate = new Date(newValue).getTime();
        if (newEndDate <= maxDateTimestamp && newEndDate >= dateRange[0] && !isNaN(newEndDate)) {
            setDateRange([dateRange[0], newEndDate]);
            if (endDateTimeoutRef.current) {
                clearTimeout(endDateTimeoutRef.current);
            }
            endDateTimeoutRef.current = setTimeout(function () {
                onDateChange(formatDate(dateRange[0]), formatDate(newEndDate));
            }, 1000);
        }
    };
    return (_jsxs(Box, __assign({ sx: { mr: 2, mt: 2 } }, { children: [_jsxs(Box, __assign({ sx: { display: 'flex', alignItems: 'center', gap: 1, mb: 2 } }, { children: [_jsx(Typography, __assign({ variant: "body2", sx: { fontSize: '0.8125rem' } }, { children: "From" })), _jsx(TextField, { type: "date", value: startDateInput, onChange: handleStartDateChange, inputProps: {
                            min: formatDate(minDateTimestamp),
                            max: formatDate(dateRange[1]),
                        }, size: "small", disabled: disabled, sx: dateInputStyles }), _jsx(Typography, __assign({ variant: "body2", sx: { fontSize: '0.8125rem' } }, { children: "to" })), _jsx(TextField, { type: "date", value: endDateInput, onChange: handleEndDateChange, inputProps: {
                            min: formatDate(dateRange[0]),
                            max: formatDate(maxDateTimestamp),
                        }, size: "small", disabled: disabled, sx: dateInputStyles })] })), _jsx(Slider, { value: dateRange, onChange: handleDateChange, onChangeCommitted: handleDateChangeCommitted, valueLabelDisplay: "off", min: minDateTimestamp, max: maxDateTimestamp, step: MILLISECONDS_PER_DAY, marks: [
                    { value: minDateTimestamp, label: formatDisplayDate(minDateTimestamp) },
                    { value: maxDateTimestamp, label: formatDisplayDate(maxDateTimestamp) },
                ], disabled: disabled, sx: {
                    color: theme.palette.primary.main,
                    '& .MuiSlider-thumb': {
                        width: 16,
                        height: 16,
                    },
                    '& .MuiSlider-markLabel': {
                        fontSize: '0.75rem',
                    },
                    '& .MuiSlider-markLabel[data-index="0"]': {
                        left: '9% !important',
                    },
                    '& .MuiSlider-markLabel[data-index="1"]': {
                        left: '90% !important',
                    },
                } })] })));
};
